import PropTypes from 'prop-types';
import {
  useCallback,
  useState,
} from 'react';

import {
  useTownAssets,
} from '../../stores';
import {
  displayJson,
  imageSrcFromId,
} from '../../utils';
import { Button } from '../buttons';
import {
  Column,
  ConfirmPopup,
  FullPopup,
  Row,
} from '../display';
import { SecuredImg } from '../graphics';

import TextInput from './TextInput';

function ImageEditor(props) {
  const {
    imageEdited,
    imageEditDone,
    version,
  } = props;
  const { assets } = useTownAssets();
  const {
    translate,
    editImage,
  } = assets;
  const [newFile, setNewFile] = useState();
  const [newFileInfo, setNewFileInfo] = useState();
  const [label, setLabel] = useState(imageEdited.label);
  const [confirmAction, setConfirmAction] = useState();

  console.log(`imageEdited=${displayJson(imageEdited, 2)}`);

  const fileOnInput = useCallback(
    async (event) => {
      if (event?.target?.files) {
        const file = event.target.files[0];

        // https://stackoverflow.com/a/39485234/9316077
        // >>>> make sure we can select the same file twice

        event.target.value = null;

        if (file?.type?.startsWith('image/')) {
          const src = URL.createObjectURL(file);

          const img = new Image();

          img.src = URL.createObjectURL(file);

          img.onload = () => {
            console.log('Image Resolution:', img.width, 'x', img.height);

            const width = img.width;
            const height = img.height;
            URL.revokeObjectURL(img.src);

            setNewFile(file);
            setNewFileInfo({
              src,
              filename: file.name,
              filetype: file.type,
              size: file.size,
              width,
              height,
            });
          };
        } else {
          setNewFile();
          setNewFileInfo();
        }
      }
    },
    [],
  );

  const imageIdSrc = imageSrcFromId(imageEdited.id, false, `?v=${version}`);

  const changeLabel = (event) => {
    setLabel(event.currentTarget.value);
  };

  const labelEdited = label.trim() !== imageEdited.label;
  const edited = labelEdited || newFile;

  const labelValid = label.trim().length > 2;

  const done = () => {
    setConfirmAction(true);
  };

  const onReject = () => {
    setConfirmAction(false);
  };

  const onConfirm = async () => {
    await editImage(
      newFile,
      {
        id: imageEdited.id,
        type: imageEdited.type,
        label: label.trim(),
      },
    );

    const imageIdSrcSimple = imageSrcFromId(imageEdited.id);

    if (navigator?.serviceWorker?.controller?.postMessage) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_CACHED_FILE',
        clrUrl: imageIdSrcSimple,
      });

      const imageIdSrcThumb = imageSrcFromId(imageEdited.id, true);

      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_CACHED_FILE',
        clrUrl: imageIdSrcThumb,
      });
    }

    imageEditDone();
  };

  return (
    <FullPopup
      onClose={imageEditDone}
      // title="Choisissez une image"
      contentClassName="flex flex-col items-start overflow-hidden gap-6"
    >
      <Row gap={6}>
        <Column framed className="h-48 w-48 shrink-0 p-1">
          <Column className="h-full w-full bg-checkers">
            <SecuredImg
              urlPath={imageIdSrc}
              alt="logo"
              className="h-full w-full object-contain"
            />
          </Column>
        </Column>

        <Column start crossStart className="self-stretch">
          <Row>
            {`${translate('label')}: ${imageEdited.label}`}
          </Row>
          <span>{`Fichier: ${newFileInfo?.filename || imageEdited.filename}`}</span>
          <span>
            {`Résolution: ${imageEdited.width} x ${imageEdited.height} pixels`}
          </span>
          <span>{`Type: ${newFileInfo?.filetype || imageEdited.filetype}`}</span>
          <span>{`Taille: ${newFileInfo?.size || imageEdited.size} bits`}</span>
        </Column>
      </Row>

      <Row gap={6}>
        <Column framed className="h-48 w-48 shrink-0 p-1">
          <Column
            className="h-full w-full bg-checkers"
          >
            {
              (newFileInfo?.src)
                ? (
                    <img
                      src={newFileInfo?.src}
                      alt="logo"
                      className="h-full w-full object-contain"
                    />
                  )
                : (
                    <SecuredImg
                      urlPath={imageIdSrc}
                      alt="logo"
                      className="h-full w-full object-contain"
                    />
                  )
            }
          </Column>

          <Button
            className="-top-1 -right-4"
            imgSrc="/images/svg/gui/edit.svg"
            id="selImage"
            fileOnInput={fileOnInput}
            noFrame
            absolute
            large
            accept={(imageEdited.type === 'comm')
              ? 'image/bmp, image/gif, image/jpeg, image/png, image/tiff, image/webp, image/x-windows-bmp'
              : 'image/bmp, image/gif, image/jpeg, image/png, image/tiff, image/webp, image/x-windows-bmp, image/svg+xml'}
          />
        </Column>

        <Column start crossStart className="self-stretch">
          <Row>
            <p className={(labelEdited) ? 'font-semibold text-pxUiOk' : undefined}>{`${translate('label')}: `}</p>
            <TextInput
              id="label"
              name="label"
              value={label}
              onChange={changeLabel}
              invalid={!labelValid}
            />
          </Row>
          <p>
            <span className={(newFileInfo) ? 'font-semibold text-pxUiOk' : undefined}>Fichier: </span>
            <span>{newFileInfo?.filename || imageEdited.filename}</span>
          </p>
          <p>
            <span className={(newFileInfo) ? 'font-semibold text-pxUiOk' : undefined}>Résolution: </span>
            <span>
              {`${newFileInfo?.width || imageEdited.width} x ${
                newFileInfo?.height || imageEdited.height} pixels`}
            </span>
          </p>
          <p>
            <span className={(newFileInfo) ? 'font-semibold text-pxUiOk' : undefined}>Type: </span>
            <span>{newFileInfo?.filetype || imageEdited.filetype}</span>
          </p>
          <p>
            <span className={(newFileInfo) ? 'font-semibold text-pxUiOk' : undefined}>Taille: </span>
            <span>{`${newFileInfo?.size || imageEdited.size} bits`}</span>
          </p>
        </Column>
      </Row>

      <Column className="w-full">
        { labelValid
          ? null
          : <i className="text-pxUiAlert">Veuillez nommer ce fichier</i>}

        {(newFileInfo?.size > (1024 * 1024 * 4.9))
          ? <i className="text-pxUiAlert">Attention: les fichiers de plus de 5Mo sont recompressés</i>
          : null}

        <Button
          label={translate('save')}
          id="select"
          disabled={!edited}
          onClick={done}
          className="mx-auto"
          baseColor
        />
      </Column>

      {(confirmAction)
        ? (
            <ConfirmPopup
              onConfirm={onConfirm}
              onReject={onReject}
              confirmText={translate('yes').toUpperCase()}
            >
              {translate('doYouWantToSaveChanges', 'statements')}
            </ConfirmPopup>
          )
        : null}

    </FullPopup>
  );
}

ImageEditor.propTypes = {
  imageEdited: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    filename: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    filetype: PropTypes.string,
    size: PropTypes.number,
  }),
  imageEditDone: PropTypes.func,
  version: PropTypes.number,
};

export default ImageEditor;
